import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionPanel, CardBody } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ReferralCommissionsList } from './ReferralCommissionsList';
import { AddReferralCommissionModal } from './modals/AddReferralCommissionModal';
import { AddSalesCommissionModal } from './modals/AddSalesCommissionModal';
import { ClientService, ReferralCommissionService, SalesCommissionService } from '/src/api';
import {
  Box,
  Card,
  CardHeader,
  CommissionIcon,
  ProgramCommissionsList,
  SalesCommissionsList,
  Text,
} from '/src/components';
import { useClientStore, useGlobalStore, useUserStore } from '/src/context';
import { ClientProgram, SalesCommission, TClientId, TReportingPeriodTypeId } from '/src/lib/models';
import { ReferralCommission } from '/src/lib/models/ReferralCommission';
import { toastMessages } from '/src/lib/toast';
import { ReportingPeriodType } from '/src/lib/types';

export type CommissionsListProps = {
  id?: string;
  title?: string;
  clientId?: TClientId;
  clientPrograms?: ClientProgram[];
  salesCommissions?: SalesCommission[];
  referralCommissions?: ReferralCommission[];
};

export const CommissionsList: React.FC<CommissionsListProps> = observer((props) => {
  const {
    id,
    title,
    clientId,
    clientPrograms: clientProgramsProp,
    salesCommissions: salesCommissionsProp,
    referralCommissions: referralCommissionsProp,
  } = props;

  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const clientStore = useClientStore();
  const { t } = useTranslation();

  /** State **/
  const [clientPrograms, setClientPrograms] = useState<ClientProgram[]>();
  const [salesCommissions, setSalesCommissions] = useState<SalesCommission[]>();
  const [activeIndexes, setActiveIndexes] = useState([0, 1, 2]);
  const [isAddingSalesCommission, setIsAddingSalesCommission] = useState(false);
  const [showAddSalesModal, setShowAddSalesModal] = useState(false);
  const [isAddingReferralCommission, setIsAddingReferralCommission] = useState(false);
  const [showAddReferralModal, setShowAddReferralModal] = useState(false);
  const [referralCommissions, setReferralCommissions] = useState<ReferralCommission[]>([]);
  const [reportingPeriodTypeId, setReportingPeriodTypeId] = useState<TReportingPeriodTypeId | undefined>(
    clientStore.selectedClient?.reporting_period_type_id
  );

  /** Computed **/
  const isLoading = !clientPrograms || !salesCommissions;
  const isYearly = reportingPeriodTypeId === ReportingPeriodType.Yearly;

  /** Methods **/
  const fetchCommissions = () => {
    if (!userStore.isInternalUser) return;

    if (clientProgramsProp && salesCommissionsProp && referralCommissionsProp) {
      setClientPrograms(clientProgramsProp);
      setSalesCommissions(salesCommissionsProp);
      setReferralCommissions(referralCommissionsProp);
      setReportingPeriodTypeId(clientStore.selectedClient?.reporting_period_type_id);
    } else if (clientId) {
      ClientService.get({ id: clientId })
        .then((client) => {
          setClientPrograms(client.client_programs);
          setSalesCommissions(client.sales_commission_rates);
          setReportingPeriodTypeId(client.reporting_period_type_id);
        })
        .catch((err) => globalStore.handleApiError(err, toastMessages.generic.error));
    }
  };

  const handleAddSalesCommission = async (userId: number, percentRate: string) => {
    if (!clientId || !clientStore.selectedClient) {
      console.error('No client or clientId available, cannot add commission');
      return;
    }

    try {
      setIsAddingSalesCommission(true);

      const startQuarter = clientStore.selectedClient.first_active_reporting_quarter;

      const requestData = {
        user_id: userId,
        percent_rate: parseFloat(percentRate),
        reporting_period_type_id: 1,
        start_reporting_quarter: startQuarter,
        end_reporting_quarter: null,
      };

      await SalesCommissionService.createSalesCommission({
        client_id: clientId,
        ...requestData,
      });

      fetchCommissions();
    } catch (error) {
      console.error('Error adding sales commission:', error);
    } finally {
      setIsAddingSalesCommission(false);
    }
  };

  const handleAddReferralCommission = async (referralPartnerId: number, percentRate: string): Promise<void> => {
    if (!clientId || !clientStore.selectedClient) {
      console.error('No client or clientId available, cannot add commission');
      return;
    }

    try {
      setIsAddingReferralCommission(true);

      const startQuarter = clientStore.selectedClient.first_active_reporting_quarter;

      const requestData = {
        client_id: clientId,
        referral_partner_id: referralPartnerId,
        percent_rate: parseFloat(percentRate),
        reporting_period_type_id: 1,
        start_reporting_quarter: startQuarter,
        end_reporting_quarter: null,
      };

      await ReferralCommissionService.createReferralCommission(requestData);

      fetchCommissions();
    } catch (error) {
      console.error('Error adding referral commission:', error);
    } finally {
      setIsAddingReferralCommission(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (isLoading) {
      fetchCommissions();
    }
  }, [isLoading]);

  /** Render **/
  return (
    <Box id={id} direction="row" gap="medium" fill="horizontal">
      <Card>
        <CardHeader title={title || 'Commission List'} icon={<CommissionIcon />} />
        <CardBody pad="none" gap="none">
          <Box elevation="small">
            <Accordion
              activeIndex={activeIndexes}
              onActive={(activeIndexes) => setActiveIndexes(activeIndexes)}
              multiple
            >
              <AccordionPanel
                label={
                  <Box pad="medium">
                    <Text size="large">{t('commissions:fuseCommissions')}</Text>
                  </Box>
                }
              >
                <ProgramCommissionsList clientPrograms={clientPrograms} isYearly={isYearly} />
              </AccordionPanel>
              <AccordionPanel
                label={
                  <Box direction="row" justify="between" align="center" pad="medium">
                    <Text size="large">{t('commissions:salesCommissions')}</Text>
                    {/* <Button
                      icon={<Add size="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddSalesModal(true);
                      }}
                      tip="Add Sales Commission"
                      disabled={isAddingSalesCommission}
                    /> */}
                  </Box>
                }
              >
                <SalesCommissionsList salesCommissions={salesCommissions} isYearly={isYearly} />
              </AccordionPanel>
              <AccordionPanel
                label={
                  <Box direction="row" justify="between" align="center" pad="medium">
                    <Text size="large">{t('commissions:referralCommissions')}</Text>
                    {/* <Button
                      icon={<Add size="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddReferralModal(true);
                      }}
                      tip="Add Referral Commission"
                      disabled={isAddingReferralCommission}
                    /> */}
                  </Box>
                }
              >
                <ReferralCommissionsList referralCommissions={referralCommissions} isYearly={isYearly} />
              </AccordionPanel>
            </Accordion>
          </Box>
        </CardBody>
      </Card>

      {/* Modals */}
      {showAddSalesModal && (
        <AddSalesCommissionModal
          onClose={() => setShowAddSalesModal(false)}
          onSubmit={handleAddSalesCommission}
          isLoading={isAddingSalesCommission}
        />
      )}

      {showAddReferralModal && (
        <AddReferralCommissionModal
          onClose={() => setShowAddReferralModal(false)}
          onSubmit={handleAddReferralCommission}
          isLoading={isAddingReferralCommission}
        />
      )}
    </Box>
  );
});
